/**
 * B2B Clients Tab Component
 * Extracted from AdminDashboard.jsx for maintainability
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Plus, Search, ArrowLeft, Pencil, Upload, UserPlus, 
  UserCheck, UserX, Send, ExternalLink, X, Mail, Copy, Check, Link as LinkIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AddUserDialog } from '@/components/ui/modal-dialog';
import { toast } from 'sonner';
import axios from '@/utils/api';

const ClientsTab = ({
  // Data
  clients,
  filteredClients,
  selectedClient,
  clientsTotals,
  clientSearchQuery,
  loadingClients,
  showBulkUploadModal,
  showAddUserModal,
  bulkUploadData,
  // Handlers
  setClientSearchQuery,
  setSelectedClient,
  setShowBulkUploadModal,
  setShowAddUserModal,
  setBulkUploadData,
  handleViewClient,
  handleResendInvite,
  handleInviteUser,
  handleBulkInvite,
  handleCSVUpload,
}) => {
  const navigate = useNavigate();
  const [sendingCredentials, setSendingCredentials] = useState(null);
  const [resendingUrls, setResendingUrls] = useState(null);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [showCredentialsMenu, setShowCredentialsMenu] = useState(null);

  // Send credentials WITH password reset
  const handleSendCredentials = async (clientId, e) => {
    if (e) e.stopPropagation();
    setShowCredentialsMenu(null);
    setSendingCredentials(clientId);
    
    try {
      const res = await axios.post(`/api/admin/clients/${clientId}/send-credentials`, {}, {
        withCredentials: true
      });
      
      if (res.data.email_sent) {
        toast.success(`Credentials sent to ${res.data.admin_username}`);
      }
      
      // Show modal with all the info
      setCredentialsModal({
        ...res.data,
        clientId
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send credentials');
    } finally {
      setSendingCredentials(null);
    }
  };

  // Resend URLs only WITHOUT password reset
  const handleResendUrls = async (clientId, e) => {
    if (e) e.stopPropagation();
    setShowCredentialsMenu(null);
    setResendingUrls(clientId);
    
    try {
      const res = await axios.post(`/api/admin/clients/${clientId}/resend-urls`, {}, {
        withCredentials: true
      });
      
      if (res.data.email_sent) {
        toast.success(`Login URLs sent (password unchanged)`);
      }
      
      // Show modal with URLs (no password shown since it wasn't reset)
      setCredentialsModal({
        ...res.data,
        clientId
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend URLs');
    } finally {
      setResendingUrls(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with stats */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#0FFFE2]" />
            B2B Licensing
          </h2>
          <p className="text-sm text-[#FDF2F8]/50 mt-1">Manage client accounts and licenses</p>
        </div>
        <Button 
          data-testid="new-client-button"
          onClick={() => navigate('/admin/b2b/new-client')}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Client
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#4A0A66]/40 to-[#2D063E]/40 rounded-xl p-4 border border-[#4A0A66]/30">
          <div className="text-2xl font-bold text-[#0FFFE2]">{clientsTotals.client_count}</div>
          <div className="text-sm text-[#FDF2F8]/50">Active Clients</div>
        </div>
        <div className="bg-gradient-to-br from-[#4A0A66]/40 to-[#2D063E]/40 rounded-xl p-4 border border-[#4A0A66]/30">
          <div className="text-2xl font-bold text-[#FF7901]">{clientsTotals.total_seats}</div>
          <div className="text-sm text-[#FDF2F8]/50">Total Seats Sold</div>
        </div>
        <div className="bg-gradient-to-br from-[#4A0A66]/40 to-[#2D063E]/40 rounded-xl p-4 border border-[#4A0A66]/30">
          <div className="text-2xl font-bold text-[#df1283]">{clientsTotals.total_activated}</div>
          <div className="text-sm text-[#FDF2F8]/50">Activated Users</div>
        </div>
        <div className="bg-gradient-to-br from-[#4A0A66]/40 to-[#2D063E]/40 rounded-xl p-4 border border-[#4A0A66]/30">
          <div className="text-2xl font-bold text-[#22c55e]">${clientsTotals.total_revenue.toLocaleString()}</div>
          <div className="text-sm text-[#FDF2F8]/50">Total Revenue</div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#FDF2F8]/50" />
        <Input
          data-testid="clients-search-input"
          placeholder="Search clients..."
          value={clientSearchQuery}
          onChange={(e) => setClientSearchQuery(e.target.value)}
          className="pl-10 bg-[#2D063E] border-[#4A0A66] text-white"
        />
      </div>

      {/* Client Detail View */}
      {selectedClient ? (
        <ClientDetailView
          client={selectedClient}
          onBack={() => setSelectedClient(null)}
          onEdit={() => navigate(`/admin/b2b/edit-client/${selectedClient.client_id}`)}
          onUploadCSV={() => setShowBulkUploadModal(true)}
          onAddUser={() => setShowAddUserModal(true)}
          onResendInvite={handleResendInvite}
          onSendCredentials={handleSendCredentials}
          onResendUrls={handleResendUrls}
          sendingCredentials={sendingCredentials}
          resendingUrls={resendingUrls}
          showCredentialsMenu={showCredentialsMenu}
          setShowCredentialsMenu={setShowCredentialsMenu}
        />
      ) : (
        /* Clients List */
        <ClientsList
          clients={filteredClients}
          allClients={clients}
          loading={loadingClients}
          onViewClient={handleViewClient}
          onSendCredentials={handleSendCredentials}
          onResendUrls={handleResendUrls}
          sendingCredentials={sendingCredentials}
          resendingUrls={resendingUrls}
          showCredentialsMenu={showCredentialsMenu}
          setShowCredentialsMenu={setShowCredentialsMenu}
        />
      )}

      {/* Credentials Modal */}
      {credentialsModal && (
        <CredentialsModal
          data={credentialsModal}
          onClose={() => setCredentialsModal(null)}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          clientName={selectedClient?.name}
          bulkUploadData={bulkUploadData}
          setBulkUploadData={setBulkUploadData}
          onClose={() => { setShowBulkUploadModal(false); setBulkUploadData([]); }}
          onFileUpload={handleCSVUpload}
          onSubmit={() => handleBulkInvite(selectedClient?.client_id)}
        />
      )}
      
      {/* Add User Modal */}
      {selectedClient && (
        <AddUserDialog
          isOpen={showAddUserModal}
          onClose={() => setShowAddUserModal(false)}
          onSubmit={(email, firstName, lastName) => {
            handleInviteUser(selectedClient.client_id, email, firstName, lastName);
          }}
          clientName={selectedClient.name}
        />
      )}
    </div>
  );
};

// Sub-component: Client Detail View
const ClientDetailView = ({ client, onBack, onEdit, onUploadCSV, onAddUser, onResendInvite, onSendCredentials, onResendUrls, sendingCredentials, resendingUrls, showCredentialsMenu, setShowCredentialsMenu }) => {
  const teamLoginUrl = `${window.location.origin}/portal/${client.client_id}/login`;
  const isLoading = sendingCredentials === client.client_id || resendingUrls === client.client_id;
  
  return (
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="text-[#FDF2F8]/50 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h3 className="text-xl font-bold text-white">{client.name}</h3>
            <p className="text-sm text-[#FDF2F8]/50">{client.contact_email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Credentials Dropdown */}
          <div className="relative">
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setShowCredentialsMenu(showCredentialsMenu === client.client_id ? null : client.client_id);
              }}
              disabled={isLoading}
              variant="outline"
              className="border-[#df1283] text-[#df1283] hover:bg-[#df1283]/20"
              data-testid="send-credentials-detail"
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Credentials ▾
            </Button>
            
            {showCredentialsMenu === client.client_id && (
              <div className="absolute right-0 mt-2 w-56 bg-[#2D063E] border border-[#4A0A66] rounded-lg shadow-xl z-50 overflow-hidden">
                <button
                  onClick={(e) => onResendUrls(client.client_id, e)}
                  className="w-full px-4 py-3 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2 border-b border-[#4A0A66]/50"
                >
                  <LinkIcon className="w-4 h-4 text-[#0FFFE2]" />
                  <div>
                    <div className="font-medium">Resend URLs Only</div>
                    <div className="text-xs text-[#FDF2F8]/50">Keep existing password</div>
                  </div>
                </button>
                <button
                  onClick={(e) => onSendCredentials(client.client_id, e)}
                  className="w-full px-4 py-3 text-left text-sm text-white hover:bg-[#4A0A66]/50 flex items-center gap-2"
                >
                  <Mail className="w-4 h-4 text-[#df1283]" />
                  <div>
                    <div className="font-medium">Reset & Send Password</div>
                    <div className="text-xs text-[#FDF2F8]/50">Generate new password</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={onEdit}
            variant="outline"
            className="border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/20"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Edit Client
          </Button>
          <Button
            onClick={onUploadCSV}
            variant="outline"
            className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload CSV
          </Button>
          <Button
            onClick={onAddUser}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>
      
      {/* Client Login URL Banner */}
      <div className="bg-[#0FFFE2]/10 border border-[#0FFFE2]/30 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LinkIcon className="w-5 h-5 text-[#0FFFE2]" />
            <div>
              <p className="text-sm font-medium text-white">Client Login URL</p>
              <p className="text-xs text-[#FDF2F8]/50">Share this with your client for their users</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="text-[#0FFFE2] text-xs bg-[#2D063E] px-3 py-2 rounded max-w-xs truncate">{teamLoginUrl}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(teamLoginUrl);
                toast.success('Client URL copied!');
              }}
              className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Client Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-white">{client.total_licenses}</div>
          <div className="text-xs text-[#FDF2F8]/50">Total Seats</div>
        </div>
        <div className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#22c55e]">{client.activated_count}</div>
          <div className="text-xs text-[#FDF2F8]/50">Activated</div>
        </div>
        <div className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#fbbf24]">{client.pending_count}</div>
          <div className="text-xs text-[#FDF2F8]/50">Pending</div>
        </div>
        <div className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#0FFFE2]">{client.remaining_seats}</div>
          <div className="text-xs text-[#FDF2F8]/50">Remaining</div>
        </div>
        <div className="bg-[#4A0A66]/30 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-[#df1283]">
            {client.users?.length > 0 
              ? Math.round((client.activated_count / client.users.length) * 100)
              : 0}%
          </div>
          <div className="text-xs text-[#FDF2F8]/50">Activation Rate</div>
        </div>
      </div>

      {/* License Info */}
      <div className="bg-[#4A0A66]/20 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#FDF2F8]/70">License Period:</span>
          <span className="text-white font-medium">
            {new Date(client.start_date).toLocaleDateString()} — {new Date(client.end_date).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-[#FDF2F8]/70">Price Paid:</span>
          <span className="text-[#22c55e] font-medium">${client.price_paid?.toLocaleString()} {client.currency}</span>
        </div>
        <div className="flex items-center justify-between text-sm mt-2">
          <span className="text-[#FDF2F8]/70">Portal Password:</span>
          <span className="text-[#FDF2F8]/50 font-mono text-xs">(Set during creation - share with client)</span>
        </div>
      </div>

      {/* Users List */}
      <div>
        <h4 className="text-lg font-semibold text-white mb-4">Users ({client.users?.length || 0})</h4>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {client.users?.length === 0 ? (
            <div className="text-center py-8 text-[#FDF2F8]/50">
              No users invited yet. Click "Add User" or "Upload CSV" to get started.
            </div>
          ) : (
            client.users?.map((user) => (
              <div 
                key={user.license_user_id}
                className="flex items-center justify-between p-3 bg-[#4A0A66]/20 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {user.status === 'activated' ? (
                    <UserCheck className="w-5 h-5 text-[#22c55e]" />
                  ) : (
                    <UserX className="w-5 h-5 text-[#fbbf24]" />
                  )}
                  <div>
                    <div className="text-white font-medium">
                      {user.first_name ? `${user.first_name} ${user.last_name || ''}` : user.email}
                    </div>
                    <div className="text-xs text-[#FDF2F8]/50">{user.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    user.status === 'activated' 
                      ? 'bg-[#22c55e]/20 text-[#22c55e]'
                      : 'bg-[#fbbf24]/20 text-[#fbbf24]'
                  }`}>
                    {user.status === 'activated' ? 'Activated' : 'Pending'}
                  </span>
                  {user.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onResendInvite(client.client_id, user.license_user_id)}
                      className="text-[#0FFFE2] hover:bg-[#0FFFE2]/20"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Sub-component: Clients List
const ClientsList = ({ clients, allClients, loading, onViewClient, onSendCredentials, onResendUrls, sendingCredentials, resendingUrls, showCredentialsMenu, setShowCredentialsMenu }) => {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="w-10 h-10 border-4 border-[#df1283] border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="text-center py-12 text-[#FDF2F8]/50">
        {allClients.length === 0 ? 'No clients yet. Click "New Client" to get started.' : 'No clients match your search.'}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {clients.map((client) => {
        const isLoading = sendingCredentials === client.client_id || resendingUrls === client.client_id;
        
        return (
        <div 
          key={client.client_id}
          onClick={() => onViewClient(client.client_id)}
          className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] p-4 cursor-pointer hover:border-[#df1283] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ 
                  background: `linear-gradient(135deg, ${client.theme?.primary_color || client.primary_color || '#df1283'}, ${client.theme?.secondary_color || client.secondary_color || '#FF7901'})`
                }}
              >
                {client.logo_url ? (
                  <img 
                    src={client.logo_url} 
                    alt={client.name}
                    className="w-full h-full object-contain rounded-lg"
                  />
                ) : (
                  client.name?.charAt(0) || 'C'
                )}
              </div>
              <div>
                <h3 className="text-white font-semibold">{client.name}</h3>
                <p className="text-sm text-[#FDF2F8]/50">{client.contact_email}</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <div className="text-white font-semibold">{client.activated_count}/{client.total_licenses}</div>
                <div className="text-[#FDF2F8]/50 text-xs">Seats</div>
              </div>
              <div className="text-center">
                <div className="text-[#22c55e] font-semibold">${client.price_paid?.toLocaleString()}</div>
                <div className="text-[#FDF2F8]/50 text-xs">Revenue</div>
              </div>
              <div className="text-center">
                <div className={`font-semibold ${
                  new Date(client.end_date) > new Date() ? 'text-[#0FFFE2]' : 'text-red-400'
                }`}>
                  {new Date(client.end_date).toLocaleDateString()}
                </div>
                <div className="text-[#FDF2F8]/50 text-xs">Expires</div>
              </div>
              <div className="flex items-center gap-2 ml-2">
                {/* Credentials Dropdown */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#df1283] text-[#df1283] hover:bg-[#df1283]/20 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCredentialsMenu(showCredentialsMenu === client.client_id ? null : client.client_id);
                    }}
                    disabled={isLoading}
                    title="Send login credentials to client"
                    data-testid={`send-credentials-${client.client_id}`}
                  >
                    {isLoading ? (
                      <div className="w-3 h-3 border-2 border-[#df1283] border-t-transparent rounded-full animate-spin mr-1" />
                    ) : (
                      <Mail className="w-3 h-3 mr-1" />
                    )}
                    Credentials ▾
                  </Button>
                  
                  {showCredentialsMenu === client.client_id && (
                    <div className="absolute right-0 mt-2 w-48 bg-[#2D063E] border border-[#4A0A66] rounded-lg shadow-xl z-50 overflow-hidden">
                      <button
                        onClick={(e) => onResendUrls(client.client_id, e)}
                        className="w-full px-3 py-2 text-left text-xs text-white hover:bg-[#4A0A66]/50 flex items-center gap-2 border-b border-[#4A0A66]/50"
                      >
                        <LinkIcon className="w-3 h-3 text-[#0FFFE2]" />
                        <div>
                          <div className="font-medium">Resend URLs Only</div>
                          <div className="text-[10px] text-[#FDF2F8]/50">Keep password</div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => onSendCredentials(client.client_id, e)}
                        className="w-full px-3 py-2 text-left text-xs text-white hover:bg-[#4A0A66]/50 flex items-center gap-2"
                      >
                        <Mail className="w-3 h-3 text-[#df1283]" />
                        <div>
                          <div className="font-medium">Reset Password</div>
                          <div className="text-[10px] text-[#FDF2F8]/50">New password</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/20 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/client-portal?preview=${client.client_id}`, '_blank');
                  }}
                  title="Preview what your B2B client sees"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Portal
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[#0FFFE2] text-[#0FFFE2] hover:bg-[#0FFFE2]/20 text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/dashboard?preview=${client.client_id}`, '_blank');
                  }}
                  title="Preview what end-users see"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )})}
    </div>
  );
};

// Sub-component: Bulk Upload Modal
const BulkUploadModal = ({ clientName, bulkUploadData, setBulkUploadData, onClose, onFileUpload, onSubmit }) => {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#4A0A66]">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Bulk Upload Users - {clientName}</h3>
            <button onClick={onClose} className="text-[#FDF2F8]/50 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-[#FDF2F8]/70">
            Upload a CSV file with columns: email, first_name (optional), last_name (optional)
          </p>
          
          <div className="border-2 border-dashed border-[#4A0A66] rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".csv"
              onChange={onFileUpload}
              className="hidden"
              id="csv-upload-clients"
            />
            <label htmlFor="csv-upload-clients" className="cursor-pointer">
              <Upload className="w-10 h-10 mx-auto mb-3 text-[#FDF2F8]/50" />
              <p className="text-[#FDF2F8]/70">Click to upload CSV</p>
            </label>
          </div>

          {bulkUploadData.length > 0 && (
            <div className="bg-[#4A0A66]/20 rounded-lg p-4">
              <p className="text-sm text-[#0FFFE2] font-medium mb-2">
                {bulkUploadData.length} users ready to invite
              </p>
              <div className="max-h-40 overflow-y-auto text-xs text-[#FDF2F8]/50 space-y-1">
                {bulkUploadData.slice(0, 10).map((user, i) => (
                  <div key={`bulk-${user.email}-${i}`}>{user.email} {user.first_name && `(${user.first_name})`}</div>
                ))}
                {bulkUploadData.length > 10 && (
                  <div>...and {bulkUploadData.length - 10} more</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[#4A0A66] flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} className="border-[#4A0A66] text-[#FDF2F8]/70">
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={bulkUploadData.length === 0}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            Send {bulkUploadData.length} Invitations
          </Button>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Credentials Modal (shows after sending credentials)
const CredentialsModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState({});
  const passwordWasReset = data.password_reset !== false; // If not explicitly false, assume password was reset
  
  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied({ ...copied, [key]: false }), 2000);
  };
  
  const copyAll = () => {
    let allText = `
Client Portal Info
========================
Portal URL: ${data.portal_url}
Client ID: ${data.admin_username}
${passwordWasReset ? `Password: ${data.new_password}` : '(Password unchanged)'}

Client Login URL (Share with clients):
${data.team_login_url}
    `.trim();
    navigator.clipboard.writeText(allText);
    toast.success('All info copied!');
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] rounded-xl border border-[#4A0A66] w-full max-w-lg">
        <div className="p-6 border-b border-[#4A0A66]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                passwordWasReset 
                  ? 'bg-gradient-to-r from-[#df1283] to-[#FF7901]' 
                  : 'bg-gradient-to-r from-[#0FFFE2] to-[#22c55e]'
              }`}>
                {passwordWasReset ? <Mail className="w-5 h-5 text-white" /> : <LinkIcon className="w-5 h-5 text-white" />}
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">
                  {passwordWasReset ? 'Credentials Sent!' : 'URLs Sent!'}
                </h3>
                <p className="text-sm text-[#FDF2F8]/50">
                  {data.email_sent 
                    ? (passwordWasReset ? 'Email delivered with new password' : 'Email delivered (password unchanged)')
                    : 'Share this info manually'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-[#FDF2F8]/50 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Admin Portal Credentials */}
          <div className="bg-[#4A0A66]/30 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-[#df1283] mb-3 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Admin Portal {passwordWasReset ? 'Credentials' : 'Info'}
            </h4>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[#FDF2F8]/70 text-sm">Portal URL:</span>
                <div className="flex items-center gap-2">
                  <code className="text-[#0FFFE2] text-xs bg-[#2D063E] px-2 py-1 rounded">{data.portal_url}</code>
                  <button onClick={() => copyToClipboard(data.portal_url, 'portal_url')} className="text-[#FDF2F8]/50 hover:text-white">
                    {copied.portal_url ? <Check className="w-4 h-4 text-[#22c55e]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-[#FDF2F8]/70 text-sm">Client ID:</span>
                <div className="flex items-center gap-2">
                  <code className="text-white text-xs bg-[#2D063E] px-2 py-1 rounded font-mono">{data.admin_username}</code>
                  <button onClick={() => copyToClipboard(data.admin_username, 'client_id')} className="text-[#FDF2F8]/50 hover:text-white">
                    {copied.client_id ? <Check className="w-4 h-4 text-[#22c55e]" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {passwordWasReset ? (
                <div className="flex items-center justify-between">
                  <span className="text-[#FDF2F8]/70 text-sm">Password:</span>
                  <div className="flex items-center gap-2">
                    <code className="text-[#FF7901] text-xs bg-[#2D063E] px-2 py-1 rounded font-mono">{data.new_password}</code>
                    <button onClick={() => copyToClipboard(data.new_password, 'password')} className="text-[#FDF2F8]/50 hover:text-white">
                      {copied.password ? <Check className="w-4 h-4 text-[#22c55e]" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-[#FDF2F8]/70 text-sm">Password:</span>
                  <span className="text-[#22c55e] text-xs bg-[#22c55e]/10 px-2 py-1 rounded">Unchanged (using existing)</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Client Login URL */}
          <div className="bg-[#0FFFE2]/10 rounded-lg p-4 border border-[#0FFFE2]/30">
            <h4 className="text-sm font-semibold text-[#0FFFE2] mb-3 flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Client Login URL
            </h4>
            <div className="flex items-center gap-2">
              <code className="text-white text-xs bg-[#2D063E] px-3 py-2 rounded flex-1 truncate">{data.team_login_url}</code>
              <button 
                onClick={() => copyToClipboard(data.team_login_url, 'team_url')} 
                className="text-[#0FFFE2] hover:bg-[#0FFFE2]/20 p-2 rounded"
              >
                {copied.team_url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-[#FDF2F8]/50 mt-2">
              Share this URL with your client. Their users will use it to log in.
            </p>
          </div>
        </div>
        
        <div className="p-6 border-t border-[#4A0A66] flex justify-between">
          <Button
            variant="outline"
            onClick={copyAll}
            className="border-[#FF7901] text-[#FF7901] hover:bg-[#FF7901]/20"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy All
          </Button>
          <Button
            onClick={onClose}
            className="bg-gradient-to-r from-[#df1283] to-[#FF7901]"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClientsTab;
